import React from "react";
import RatingStars from "./RatingStars";
import { RATING_LOW_THRESHOLD } from "../../types/Rating";
import classes from "./rating.module.scss";

interface SingleRatingBadgeProps {
    /** Average rating. null/undefined means no ratings yet. */
    average: number | null | undefined;
    /** Total number of ratings (for confidence display). */
    total: number;
    /** Show stars in addition to the numeric value. */
    showStars?: boolean;
    /** Compact mode hides the count (rendered as "★ 4.3"). */
    compact?: boolean;
    /** Highlight low ratings in red as a visual warning. */
    highlightLow?: boolean;
    /** Optional label prefix shown before stars (e.g. "Ops"). */
    label?: string;
    operational?: undefined;
    operationalCount?: undefined;
    financial?: undefined;
    financialCount?: undefined;
    hideFinancial?: undefined;
}

interface SplitRatingBadgeProps {
    /** Operational rating (mission/standalone contexts). */
    operational: number | null | undefined;
    operationalCount: number;
    /** Financial rating (invoice contexts). */
    financial: number | null | undefined;
    financialCount: number;
    /** The payment rating is finance's: true when the viewer may not see it (the API left it out). */
    hideFinancial?: boolean;
    showStars?: boolean;
    compact?: boolean;
    highlightLow?: boolean;
    average?: undefined;
    total?: undefined;
    label?: undefined;
}

type RatingBadgeProps = SingleRatingBadgeProps | SplitRatingBadgeProps;

/**
 * Compact rating badge for inline use in lists, cards, dropdowns, etc.
 *
 * Two modes:
 *   • single — pass `average` + `total`. Used wherever a single number is the
 *     right signal (asset/operator pages, single-context badges).
 *   • split  — pass `operational` + `financial` (and counts). Used on
 *     organization-level surfaces so mission planners and finance can see
 *     both numbers and weigh the trade-off when picking providers/clients.
 */
const RatingBadge: React.FC<RatingBadgeProps> = (props) => {
    const isSplit = "operational" in props || "financial" in props;
    const showStars = props.showStars ?? true;
    const compact = props.compact ?? false;
    const highlightLow = props.highlightLow ?? true;

    const renderOne = (
        avg: number | null | undefined,
        total: number,
        prefix?: string,
        icon?: string,
    ): React.ReactNode => {
        // Tooltip carries the full label even when the visible text is
        // icon-only — so screen readers and hover still get "Operational: 3.7".
        const tooltipPrefix = prefix ?? "Average";

        if (avg === null || avg === undefined || total === 0) {
            return (
                <span
                    className={classes.notRatedPill}
                    title={`${tooltipPrefix}: no ratings yet`}
                >
                    {icon && <i className={icon} style={{ marginRight: 4 }} />}
                    {compact ? "—" : (prefix ? `${prefix}: —` : "Not yet rated")}
                </span>
            );
        }
        const value = Number(avg);
        // Tier the numeric color so a score reads "good / meh / bad" at a
        // glance. Thresholds match the dimension breakdown bars elsewhere
        // (≥4 high, ≥2.5 mid, otherwise low). highlightLow keeps backwards
        // compat — when off, the number stays on the neutral header color.
        let tierClass = "";
        if (highlightLow) {
            if (value < RATING_LOW_THRESHOLD) tierClass = classes.ratingValueLow;
            else if (value < 4) tierClass = classes.ratingValueMid;
            else tierClass = classes.ratingValueHigh;
        }
        // Compact = icon + number only. The icon is the category indicator
        // (wrench = ops, cash = financial) — text labels would be redundant
        // and cause dropdown overflow. Tooltip retains the full description.
        const showStarsHere = showStars && !compact;
        return (
            <span
                className={`${classes.badgeContainer} ${compact ? classes.badgePill : ""}`}
                title={`${tooltipPrefix}: ${value.toFixed(2)} from ${total} rating${total === 1 ? "" : "s"}`}
            >
                {icon && <i className={icon} style={{ marginRight: 4, fontSize: "var(--fs-xs)" }} />}
                {!compact && prefix && (
                    <small style={{ marginRight: 4, opacity: 0.75 }}>{prefix}</small>
                )}
                {showStarsHere && <RatingStars value={value} size={14} />}
                <span className={`${classes.ratingValue} ${tierClass}`}>
                    {value.toFixed(1)}
                </span>
                {!compact && (
                    <small className={classes.dimensionCount}>({total})</small>
                )}
            </span>
        );
    };

    if (isSplit) {
        const p = props as SplitRatingBadgeProps;
        // Stack vertically in non-compact lists, inline in compact dropdowns.
        return (
            <span
                className={compact ? "d-inline-flex flex-wrap" : "d-inline-flex flex-column"}
                style={{ gap: compact ? 8 : 2, lineHeight: 1.2 }}
            >
                {renderOne(p.operational, p.operationalCount, "Ops", "bi bi-tools")}
                {!p.hideFinancial && renderOne(p.financial, p.financialCount, "Payment", "bi bi-cash-coin")}
            </span>
        );
    }

    const s = props as SingleRatingBadgeProps;
    return <>{renderOne(s.average, s.total, s.label)}</>;
};

export default RatingBadge;
